import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import PublicLanguageSwitcher from '../ui/PublicLanguageSwitcher';
import Button from '../ui/Button';
import ViewportScenes, { SceneGroup, SceneHeader, SceneReveal, SceneShell, ViewportScene } from '../ui/ViewportScenes';
import HeroDepthBackground from '../ui/HeroDepthBackground';
import SceneProgressIndicator from '../ui/SceneProgressIndicator';
import {
  Users,
  TrendingUp,
  Award,
  Shield,
  Zap,
  BarChart3,
  Gift,
  Target,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Globe,
  Lock,
  Rocket,
  Menu,
  X,
} from 'lucide-react';
import { navigateToAppPath } from '../../utils/domainRouting';

const LANDING_SCENE_IDS = [
  'hero',
  'capabilities',
  'features',
  'benefits',
  'pricing',
  'contact',
  'faq',
  'footer',
];

const NAV_SCENE_MAP = {
  features: 'features',
  benefits: 'benefits',
  pricing: 'pricing',
  contact: 'contact',
  faq: 'faq',
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSceneIndex, setActiveSceneIndex] = useState(0);
  const [activeSceneLabel, setActiveSceneLabel] = useState(() => t.landing.sceneNav?.hero || 'Home');
  const sceneApiRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const navigateToLogin = () => {
    if (!navigateToAppPath('/login')) {
      navigate('/login');
    }
  };

  const navigateToEnterprise = () => {
    if (!navigateToAppPath('/enterprise')) {
      navigate('/enterprise');
    }
  };

  const scrollToSection = (sectionId) => {
    sceneApiRef.current?.scrollToScene?.(sectionId);
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    sceneApiRef.current?.scrollToScene?.(0);
  };

  const getSceneLabel = useCallback(
    (id) => t.landing.sceneNav?.[id] || id,
    [t]
  );

  const handleSceneChange = useCallback(
    (index) => {
      setIsScrolled(index > 0);
      setShowScrollTop(index > 0);
      setActiveSceneIndex(index);
      setActiveSceneLabel(getSceneLabel(LANDING_SCENE_IDS[index]));
    },
    [getSceneLabel]
  );

  useEffect(() => {
    if (!window.location.hash) return undefined;

    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    return undefined;
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const menu = mobileMenuRef.current;
    if (!menu) return undefined;

    const focusable = menu.querySelectorAll('a, button');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab' || !first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = (event) => {
      if (
        mobileMenuRef.current?.contains(event.target) ||
        menuButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setMobileMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [mobileMenuOpen]);

  const navLinkClass = (sectionId) => {
    const isActive = LANDING_SCENE_IDS[activeSceneIndex] === NAV_SCENE_MAP[sectionId];
    return `nav-link nav-link--compact hover:text-orange-500 shrink-0 ${
      isActive ? 'nav-link--active' : ''
    }`;
  };

  const features = [
    {
      icon: Users,
      title: t.landing.features.referralManagement.title,
      description: t.landing.features.referralManagement.description,
      gradient: 'from-orange-500 to-amber-500'
    },
    {
      icon: TrendingUp,
      title: t.landing.features.growthAnalytics.title,
      description: t.landing.features.growthAnalytics.description,
      gradient: 'from-amber-500 to-orange-600'
    },
    {
      icon: Award,
      title: t.landing.features.rewardSystem.title,
      description: t.landing.features.rewardSystem.description,
      gradient: 'from-orange-600 to-red-500'
    },
    {
      icon: Shield,
      title: t.landing.features.fraudPrevention.title,
      description: t.landing.features.fraudPrevention.description,
      gradient: 'from-red-500 to-orange-500'
    },
    {
      icon: Zap,
      title: t.landing.features.instantAutomation.title,
      description: t.landing.features.instantAutomation.description,
      gradient: 'from-orange-500 to-amber-400'
    },
    {
      icon: BarChart3,
      title: t.landing.features.realtimeReports.title,
      description: t.landing.features.realtimeReports.description,
      gradient: 'from-amber-400 to-orange-500'
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: t.landing.benefits.increaseSales.title,
      description: t.landing.benefits.increaseSales.description,
      stat: t.landing.benefits.increaseSales.stat
    },
    {
      icon: Users,
      title: t.landing.benefits.customerGrowth.title,
      description: t.landing.benefits.customerGrowth.description,
      stat: t.landing.benefits.customerGrowth.stat
    },
    {
      icon: Gift,
      title: t.landing.benefits.customerLoyalty.title,
      description: t.landing.benefits.customerLoyalty.description,
      stat: t.landing.benefits.customerLoyalty.stat
    },
    {
      icon: Rocket,
      title: t.landing.benefits.fastDeployment.title,
      description: t.landing.benefits.fastDeployment.description,
      stat: t.landing.benefits.fastDeployment.stat
    }
  ];

  const plans = t.landing.pricing.plans || [];

  const contactMethods = [
    {
      icon: Globe,
      title: t.landing.contact.methods.support.title,
      description: t.landing.contact.methods.support.description,
      contact: 'support@enrola.com',
      action: 'mailto:support@enrola.com',
    },
    {
      icon: Users,
      title: t.landing.contact.methods.sales.title,
      description: t.landing.contact.methods.sales.description,
      contact: 'sales@enrola.com',
      action: 'mailto:sales@enrola.com',
    },
    {
      icon: Shield,
      title: t.landing.contact.methods.tech.title,
      description: t.landing.contact.methods.tech.description,
      contact: 'tech@enrola.com',
      action: 'mailto:tech@enrola.com',
    },
  ];

  const faqs = t.landing.faq.items || [];
  const [openFaq, setOpenFaq] = useState(null);

  const renderFeatureCard = (feature) => (
    <SceneReveal
      key={feature.title}
      className="group card-premium landing-scene-card max-lg:landing-scene-card--compact hover-lift"
    >
      <div
        className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${feature.gradient} shadow-elegant transition-transform duration-300 group-hover:scale-110 max-lg:mb-2.5 max-lg:h-10 max-lg:w-10 sm:mb-2 sm:h-9 sm:w-9 lg:mb-4 lg:h-12 lg:w-12 lg:rounded-2xl`}
      >
        <feature.icon className="h-4 w-4 text-white max-lg:h-5 max-lg:w-5 lg:h-6 lg:w-6" />
      </div>
      <h3 className="mb-1 text-xs font-bold leading-snug text-secondary-900 max-lg:text-sm sm:text-sm lg:mb-2 lg:text-xl">{feature.title}</h3>
      <p className="text-[11px] leading-snug text-secondary-600 max-lg:line-clamp-2 max-lg:text-xs sm:text-xs lg:text-sm lg:leading-relaxed">{feature.description}</p>
    </SceneReveal>
  );

  const renderFaqItem = (faq, index) => {
    const isOpen = openFaq === index;

    return (
    <SceneReveal
      key={faq.question}
      className="card-premium self-start overflow-hidden rounded-xl"
    >
      <button
        type="button"
        onClick={() => setOpenFaq(isOpen ? null : index)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-3 py-3 text-left transition-colors hover:bg-white/50 max-lg:px-4 max-lg:py-3.5 sm:px-4 sm:py-3"
      >
        <span className="pr-2 text-sm font-semibold leading-snug text-secondary-900 max-lg:text-base sm:text-sm">{faq.question}</span>
        <div
          className={`shrink-0 transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <svg className="h-4 w-4 text-orange-500 max-lg:h-5 max-lg:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-3 pb-3 pt-0 text-xs leading-relaxed text-secondary-600 max-lg:px-4 max-lg:pb-4 max-lg:text-sm sm:px-4 sm:pb-4 sm:text-sm">
            {faq.answer}
          </div>
        </div>
      </div>
    </SceneReveal>
    );
  };

  return (
    <div className="relative h-[100dvh] overflow-hidden">
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {activeSceneLabel}
      </div>

      {/* Navigation */}
      <nav className={`safe-top fixed top-0 w-full z-50 transition-[background-color,box-shadow,border-color,backdrop-filter] duration-300 ${
        isScrolled || mobileMenuOpen
          ? 'glass-strong border-b border-orange-200/30 shadow-soft '
          : 'border-b border-white/20 bg-white/45 backdrop-blur-md  '
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3 sm:h-20">
            {/* Logo */}
            <div className="flex items-center shrink-0">
              <img
                src="/logo.png"
                alt={t.brand?.logoAlt || 'Enrola Logo'}
                className="h-10 object-contain"
              />
            </div>

            {/* Desktop: links scroll + fixed CTAs (language last) */}
            <div className="hidden lg:flex items-center flex-1 min-w-0 justify-end gap-2 xl:gap-4">
              <div className="flex items-center gap-1 xl:gap-3 min-w-0 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <a href="#features" className={navLinkClass('features')} aria-current={LANDING_SCENE_IDS[activeSceneIndex] === 'features' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>{t.landing.nav.features}</a>
                <a href="#benefits" className={navLinkClass('benefits')} aria-current={LANDING_SCENE_IDS[activeSceneIndex] === 'benefits' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); scrollToSection('benefits'); }}>{t.landing.nav.benefits}</a>
                <a href="#pricing" className={navLinkClass('pricing')} aria-current={LANDING_SCENE_IDS[activeSceneIndex] === 'pricing' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>{t.landing.nav.pricing}</a>
                <button type="button" onClick={navigateToEnterprise} className="nav-link nav-link--compact hover:text-orange-500 font-semibold text-orange-600 shrink-0">{t.landing.nav.forBusiness}</button>
                <a href="#contact" className={navLinkClass('contact')} aria-current={LANDING_SCENE_IDS[activeSceneIndex] === 'contact' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t.landing.nav.contact}</a>
                <a href="#faq" className={navLinkClass('faq')} aria-current={LANDING_SCENE_IDS[activeSceneIndex] === 'faq' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>{t.landing.nav.faq}</a>
              </div>

              <div className="flex items-center gap-2 xl:gap-3 shrink-0 pl-2 xl:pl-3 border-l border-secondary-200/60 ">
                <Button
                  variant="ghost"
                  size="compact"
                  onClick={navigateToLogin}
                  className="whitespace-nowrap"
                >
                  {t.landing.nav.signIn}
                </Button>
                <Button
                  variant="primary"
                  size="compact"
                  onClick={navigateToLogin}
                  icon={ArrowRight}
                  iconPosition="right"
                  className="whitespace-nowrap"
                >
                  {t.landing.nav.getStarted}
                </Button>
                <PublicLanguageSwitcher compact />
              </div>
            </div>

            {/* Mobile / tablet menu + language at far right */}
            <div className="lg:hidden flex items-center gap-2 shrink-0 ml-auto">
              <button
                ref={menuButtonRef}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="touch-target p-2 rounded-xl hover:bg-secondary-100 transition-colors"
                aria-expanded={mobileMenuOpen}
                aria-controls="landing-mobile-menu"
                aria-label={mobileMenuOpen ? t.common.close : 'Menu'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <PublicLanguageSwitcher compact />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen ? (
            <motion.div
              id="landing-mobile-menu"
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
              className="lg:hidden overflow-hidden border-t border-secondary-200/50 bg-white/95 backdrop-blur-xl  "
            >
              <div className="safe-bottom space-y-1 px-4 py-4 sm:space-y-4 sm:py-6">
                <a href="#features" className={`touch-target block py-3 ${LANDING_SCENE_IDS[activeSceneIndex] === 'features' ? 'rounded-xl bg-orange-50 px-3 font-semibold text-orange-700' : 'rounded-xl px-3 text-secondary-600 hover:bg-orange-50/60 hover:text-orange-700'}`} onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>{t.landing.nav.features}</a>
                <a href="#benefits" className={`touch-target block py-3 ${LANDING_SCENE_IDS[activeSceneIndex] === 'benefits' ? 'rounded-xl bg-orange-50 px-3 font-semibold text-orange-700' : 'rounded-xl px-3 text-secondary-600 hover:bg-orange-50/60 hover:text-orange-700'}`} onClick={(e) => { e.preventDefault(); scrollToSection('benefits'); }}>{t.landing.nav.benefits}</a>
                <button type="button" className="touch-target block w-full py-3 text-left font-semibold text-orange-600" onClick={() => { setMobileMenuOpen(false); navigateToEnterprise(); }}>{t.landing.nav.forBusiness}</button>
                <a href="#pricing" className={`touch-target block py-3 ${LANDING_SCENE_IDS[activeSceneIndex] === 'pricing' ? 'rounded-xl bg-orange-50 px-3 font-semibold text-orange-700' : 'rounded-xl px-3 text-secondary-600 hover:bg-orange-50/60 hover:text-orange-700'}`} onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>{t.landing.nav.pricing}</a>
                <a href="#contact" className={`touch-target block py-3 ${LANDING_SCENE_IDS[activeSceneIndex] === 'contact' ? 'rounded-xl bg-orange-50 px-3 font-semibold text-orange-700' : 'rounded-xl px-3 text-secondary-600 hover:bg-orange-50/60 hover:text-orange-700'}`} onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t.landing.nav.contact}</a>
                <a href="#faq" className={`touch-target block py-3 ${LANDING_SCENE_IDS[activeSceneIndex] === 'faq' ? 'rounded-xl bg-orange-50 px-3 font-semibold text-orange-700' : 'rounded-xl px-3 text-secondary-600 hover:bg-orange-50/60 hover:text-orange-700'}`} onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>{t.landing.nav.faq}</a>
                <div className="space-y-3 pt-4">
                  <Button type="button" variant="ghost" onClick={navigateToLogin} className="w-full">
                    {t.landing.nav.signIn}
                  </Button>
                  <Button type="button" variant="primary" onClick={navigateToLogin} className="w-full">
                    {t.landing.nav.getStarted}
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </nav>

      <SceneProgressIndicator
        sceneIds={LANDING_SCENE_IDS}
        activeIndex={activeSceneIndex}
        onSelect={scrollToSection}
        getLabel={getSceneLabel}
      />

      <ViewportScenes
        sceneApiRef={sceneApiRef}
        onSceneChange={handleSceneChange}
        className="relative z-10"
        renderBackground={(sceneCount, activeIndex) => (
          <HeroDepthBackground sceneCount={sceneCount} visibleSceneIndex={activeIndex} />
        )}
      >
        {/* 1 — Hero */}
        <ViewportScene id="hero">
          <SceneShell>
            <SceneGroup className="mx-auto max-w-4xl text-center">
              <SceneReveal className="mb-2 inline-flex items-center space-x-2 rounded-full border border-white/50 bg-white/80 px-4 py-2.5 shadow-soft backdrop-blur-sm max-lg:mb-3 max-lg:px-5 max-lg:py-3 sm:mb-3 lg:mb-4 lg:px-6 lg:py-2.5">
                <Sparkles className="h-4 w-4 text-orange-500 max-lg:h-5 max-lg:w-5" />
                <span className="text-xs font-semibold text-secondary-700 max-lg:text-sm sm:text-sm">{t.landing.hero.badge}</span>
              </SceneReveal>
              <SceneReveal>
                <h1 className="mb-2 text-2xl font-bold leading-tight max-lg:mb-3 sm:mb-3 sm:text-4xl lg:mb-4 lg:text-5xl xl:text-6xl">
                  <span className="text-orange-600 ">{t.landing.hero.title1}</span>
                  <br />
                  <span className="text-secondary-900">{t.landing.hero.title2}</span>
                </h1>
              </SceneReveal>
              <SceneReveal>
                <p className="mx-auto mb-3 max-w-3xl text-base leading-snug text-secondary-600 max-lg:mb-4 sm:mb-4 sm:text-base lg:mb-6 lg:max-w-2xl lg:text-lg">
                  {t.landing.hero.subtitle}
                </p>
              </SceneReveal>
              <SceneReveal className="mb-3 flex flex-col items-center justify-center gap-2.5 max-lg:mb-4 max-lg:gap-3 sm:flex-row sm:gap-3 lg:mb-4">
                <Button
                  type="button"
                  variant="primary"
                  onClick={navigateToLogin}
                  icon={ArrowRight}
                  iconPosition="right"
                  className="w-full max-lg:px-7 sm:w-auto lg:px-8"
                >
                  {t.landing.hero.startTrial}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => scrollToSection('pricing')}
                  className="w-full max-lg:px-7 sm:w-auto lg:px-8"
                >
                  {t.landing.hero.viewPricing}
                </Button>
              </SceneReveal>
              <SceneReveal className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-secondary-500 max-lg:gap-x-5 max-lg:text-sm sm:gap-6 sm:text-xs lg:text-sm">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success-500 max-lg:h-[1.125rem] max-lg:w-[1.125rem] sm:h-4 sm:w-4" />
                  <span>{t.landing.hero.noCreditCard}</span>
                </div>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success-500 max-lg:h-[1.125rem] max-lg:w-[1.125rem] sm:h-4 sm:w-4" />
                  <span>{t.landing.hero.freeTrial}</span>
                </div>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success-500 max-lg:h-[1.125rem] max-lg:w-[1.125rem] sm:h-4 sm:w-4" />
                  <span>{t.landing.hero.cancelAnytime}</span>
                </div>
              </SceneReveal>
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* 2 — Capabilities */}
        <ViewportScene id="capabilities" className="landing-scene-glass">
          <SceneShell>
            <SceneGroup className="landing-scene-grid grid-cols-2 md:grid-cols-4">
              {[
                { value: t.landing.capabilities.unlimited, label: t.landing.capabilities.scalability },
                { value: t.landing.capabilities.realtime, label: t.landing.capabilities.processing },
                { value: t.landing.capabilities.uptime, label: t.landing.capabilities.sla },
                { value: t.landing.capabilities.enterprise, label: t.landing.capabilities.ready },
              ].map((item) => (
                <SceneReveal key={item.label} className="text-center">
                  <div className="landing-scene-stat mb-1">{item.value}</div>
                  <div className="landing-scene-stat-label">{item.label}</div>
                </SceneReveal>
              ))}
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* 3 — Features */}
        <ViewportScene id="features" className="landing-scene-glass">
          <SceneShell>
            <SceneHeader
              compact
              title={t.landing.features.title}
              titleHighlight={t.landing.features.titleHighlight}
              subtitle={t.landing.features.subtitle}
            />
            <SceneGroup className="landing-scene-grid grid-cols-2 md:grid-cols-3">
              {features.map((feature) => renderFeatureCard(feature))}
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* Benefits */}
        <ViewportScene id="benefits" className="landing-scene-glass">
          <SceneShell>
            <SceneHeader
              compact
              title={t.landing.benefits.title}
              titleHighlight={t.landing.benefits.titleHighlight}
              subtitle={t.landing.benefits.subtitle}
            />
            <SceneGroup className="landing-scene-grid grid-cols-2">
              {benefits.map((benefit) => (
                <SceneReveal key={benefit.title} className="group text-center">
                  <div className="card-premium landing-scene-card max-lg:landing-scene-card--compact hover-lift">
                    <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-elegant transition-transform duration-300 group-hover:scale-110 max-lg:mb-2.5 max-lg:h-10 max-lg:w-10 sm:mb-2 sm:h-9 sm:w-9 lg:mb-4 lg:h-12 lg:w-12 lg:rounded-2xl">
                      <benefit.icon className="h-4 w-4 text-white max-lg:h-5 max-lg:w-5 lg:h-6 lg:w-6" />
                    </div>
                    <div className="mb-1 text-lg font-bold leading-none text-orange-600  max-lg:text-xl sm:text-lg lg:mb-2 lg:text-2xl">
                      {benefit.stat}
                    </div>
                    <h3 className="mb-1 text-xs font-bold leading-snug text-secondary-900 max-lg:text-sm sm:text-sm lg:mb-2 lg:text-xl">{benefit.title}</h3>
                    <p className="text-[11px] leading-snug text-secondary-600 max-lg:line-clamp-2 max-lg:text-xs sm:text-xs lg:text-sm lg:leading-relaxed">{benefit.description}</p>
                  </div>
                </SceneReveal>
              ))}
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* Pricing */}
        <ViewportScene id="pricing" className="landing-scene-glass">
          <SceneShell>
            <SceneHeader
              compact
              title={t.landing.pricing.title}
              titleHighlight={t.landing.pricing.titleHighlight}
              subtitle={t.landing.pricing.subtitle}
            />
            <SceneGroup className="landing-scene-grid landing-pricing-grid max-lg:grid-cols-1 max-lg:gap-2 lg:grid-cols-3">
              {plans.map((plan) => {
                const isCustom = plan.id === 'enterprise' || plan.price === 'Custom';
                const displayPrice = isCustom ? t.landing.pricing.customPrice : `$${plan.price}`;
                const periodLabel = plan.period ? `/${t.landing.pricing.perMonth}` : '';
                const trialCtaLabel = t.landing.pricing.startTrialShort || t.landing.nav.getStarted;

                return (
                <SceneReveal
                  key={plan.id || plan.name}
                  className={`relative max-lg:w-full ${
                    plan.popular ? 'max-lg:order-first' : ''
                  } ${
                    plan.popular
                      ? 'card-premium bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-glow-lg max-lg:ring-2 max-lg:ring-orange-300/60'
                      : 'card-premium'
                  }`}
                >
                  <div className="landing-scene-card max-lg:landing-scene-card--compact max-lg:flex max-lg:w-full max-lg:items-center max-lg:justify-between max-lg:gap-3 lg:block">
                    <div className="max-lg:min-w-0 max-lg:flex-1">
                      <h3 className={`mb-1 text-xs font-bold leading-tight max-lg:mb-0.5 max-lg:text-sm sm:text-sm lg:mb-1 lg:text-xl ${plan.popular ? 'text-white' : 'text-secondary-900'}`}>
                        {plan.name}
                      </h3>
                      <p className={`mb-2.5 text-sm max-lg:sr-only sm:mb-2.5 lg:mb-3 ${plan.popular ? 'text-white/90' : 'text-secondary-600'}`}>
                        {plan.description}
                      </p>
                      <div className="mb-2.5 max-lg:mb-0 sm:mb-2.5 lg:mb-4">
                        <div className="flex flex-wrap items-baseline gap-x-1">
                          <span className={`text-xl font-bold leading-none max-lg:text-lg sm:text-xl lg:text-4xl ${plan.popular ? 'text-white' : 'text-secondary-900'}`}>
                            {displayPrice}
                          </span>
                          {periodLabel ? (
                            <span className={`text-xs max-lg:text-[11px] sm:text-xs lg:text-sm ${plan.popular ? 'text-white/80' : 'text-secondary-600'}`}>
                              {periodLabel}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <ul className="mb-5 hidden space-y-2 lg:block">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start text-sm">
                          <CheckCircle2
                            className={`mr-2 mt-0.5 h-4 w-4 shrink-0 ${
                              plan.popular ? 'text-white' : 'text-orange-500'
                            }`}
                          />
                          <span className={plan.popular ? 'text-white/90' : 'text-secondary-600'}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={isCustom ? () => scrollToSection('contact') : navigateToLogin}
                      className={`w-full shrink-0 rounded-lg py-2.5 text-xs font-semibold leading-tight transition-[color,background-color,box-shadow] duration-300 max-lg:w-auto max-lg:whitespace-nowrap max-lg:px-3 max-lg:py-2 max-lg:text-[11px] sm:text-xs lg:rounded-xl lg:py-3 lg:text-sm ${
                        plan.popular
                          ? 'bg-white text-orange-600 hover:bg-white/90 shadow-elegant'
                          : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-elegant hover:shadow-glow'
                      }`}
                    >
                      <span className="lg:hidden">
                        {isCustom ? t.landing.pricing.contactSalesShort || t.landing.pricing.contactSales : trialCtaLabel}
                      </span>
                      <span className="hidden lg:inline">
                        {isCustom ? t.landing.pricing.contactSales : t.landing.pricing.startTrial}
                      </span>
                    </button>
                  </div>
                </SceneReveal>
                );
              })}
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* Contact */}
        <ViewportScene id="contact" className="landing-scene-glass">
          <SceneShell>
            <SceneHeader
              compact
              title={t.landing.contact.title}
              titleHighlight={t.landing.contact.titleHighlight}
              subtitle={t.landing.contact.subtitle}
            />
            <SceneGroup className="landing-scene-grid grid-cols-3">
              {contactMethods.map((method) => (
                <SceneReveal key={method.title} className="card-premium landing-scene-card max-lg:landing-scene-card--compact text-center hover-lift">
                  <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-elegant max-lg:mb-2.5 max-lg:h-10 max-lg:w-10 sm:mb-2 sm:h-9 sm:w-9 lg:mb-4 lg:h-12 lg:w-12 lg:rounded-2xl">
                    <method.icon className="h-4 w-4 text-white max-lg:h-5 max-lg:w-5 lg:h-6 lg:w-6" />
                  </div>
                  <h3 className="mb-1 text-xs font-bold leading-snug text-secondary-900 max-lg:text-sm sm:text-xs lg:mb-2 lg:text-xl">{method.title}</h3>
                  <p className="mb-3 text-sm text-secondary-600 max-lg:sr-only">{method.description}</p>
                  <a
                    href={method.action}
                    className="break-all text-xs font-semibold leading-tight text-orange-600 transition-colors hover:text-orange-700 max-lg:text-sm sm:text-xs lg:break-normal lg:text-sm"
                  >
                    {method.contact}
                  </a>
                </SceneReveal>
              ))}
            </SceneGroup>
            <SceneReveal className="card-premium landing-scene-card mx-auto mt-2 max-w-2xl text-center max-lg:mt-3 sm:mt-3 lg:mt-8">
              <h3 className="mb-2 text-sm font-bold text-secondary-900 max-lg:text-base sm:text-sm lg:mb-3 lg:text-2xl">
                {t.landing.contact.immediate}
              </h3>
              <p className="mb-5 text-sm text-secondary-600 max-lg:sr-only sm:text-base">{t.landing.contact.immediateDesc}</p>
              <div className="flex flex-col items-center justify-center gap-2.5 max-lg:gap-3 sm:flex-row sm:gap-3">
                <Button
                  href="mailto:support@enrola.com"
                  variant="primary"
                  icon={ArrowRight}
                  iconPosition="right"
                  className="w-full text-sm max-lg:py-2.5 sm:w-auto sm:text-sm"
                >
                  {t.landing.contact.contactSupport}
                </Button>
                <Button
                  href="mailto:sales@enrola.com"
                  variant="secondary"
                  className="w-full text-sm max-lg:py-2.5 sm:w-auto sm:text-sm"
                >
                  {t.landing.contact.salesInquiry}
                </Button>
              </div>
            </SceneReveal>
          </SceneShell>
        </ViewportScene>

        {/* FAQ */}
        <ViewportScene id="faq" className="landing-scene-glass">
          <SceneShell>
            <SceneHeader
              compact
              title={t.landing.faq.title}
              titleHighlight={t.landing.faq.titleHighlight}
              subtitle={t.landing.faq.subtitle}
            />
            <SceneGroup className="mx-auto grid w-full max-w-4xl grid-cols-1 items-start gap-2 max-lg:gap-2.5 sm:grid-cols-2 sm:gap-2">
              {faqs.map((faq, index) => renderFaqItem(faq, index))}
            </SceneGroup>
          </SceneShell>
        </ViewportScene>

        {/* CTA + Footer */}
        <ViewportScene id="footer" className="landing-scene-glass landing-scene-glass--footer">
          <SceneShell>
            <SceneGroup className="mx-auto flex w-full max-w-5xl min-h-0 flex-col gap-2 sm:gap-3 lg:gap-6">
            <SceneReveal className="card-premium bg-gradient-to-br from-orange-500 to-amber-500 p-4 text-center sm:p-5 lg:p-8 xl:p-10">
                <h2 className="mb-2 text-base font-bold leading-snug text-white sm:text-lg lg:mb-4 lg:text-3xl xl:text-4xl">{t.landing.cta.title}</h2>
                <p className="mx-auto mb-5 max-w-2xl text-sm text-white/90 max-lg:sr-only sm:text-base">{t.landing.cta.subtitle}</p>
                <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
                  <button
                    type="button"
                    onClick={navigateToLogin}
                    className="w-full rounded-lg bg-white px-4 py-2.5 text-xs font-semibold text-orange-600 shadow-elegant transition-[background-color,transform,box-shadow] duration-300 hover:scale-105 hover:bg-white/90 sm:w-auto sm:text-sm lg:px-6 lg:py-3"
                  >
                    {t.landing.cta.startTrial}
                    <ArrowRight className="ml-1.5 inline h-3.5 w-3.5" />
                  </button>
                  <a
                    href="#pricing"
                    className="w-full rounded-lg border border-white/30 bg-white/20 px-4 py-2.5 text-xs font-semibold text-white backdrop-blur-sm transition-[background-color,border-color] duration-300 hover:bg-white/30 sm:w-auto sm:text-sm lg:px-6 lg:py-3"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('pricing');
                    }}
                  >
                    {t.landing.cta.viewPricing}
                  </a>
                </div>
                <p className="mt-4 text-xs text-white/80 max-lg:sr-only sm:text-sm">{t.landing.cta.trialNote}</p>
              </SceneReveal>

            <SceneReveal as="footer" className="relative z-10 flex w-full flex-col gap-8 rounded-3xl bg-secondary-900 px-6 py-8 text-white sm:px-8 lg:hidden">
        <div className="mx-auto w-full max-w-7xl">
          <div className="mb-6 flex items-center justify-center sm:mb-8">
            <img
              src="/logo.png"
              alt={t.brand?.logoAlt || 'Enrola Logo'}
              className="h-8 w-auto object-contain sm:h-10"
            />
          </div>
          <div className="grid grid-cols-1 gap-8 text-center text-sm sm:grid-cols-3 sm:text-left">
            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.product}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('features')}>{t.landing.footer.features}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('pricing')}>{t.landing.footer.pricing}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('faq')}>{t.landing.footer.integrations}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.api}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.company}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('features')}>{t.landing.footer.aboutUs}</button></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('faq')}>{t.landing.footer.blog}</button></li>
                <li><a href="mailto:sales@enrola.com" className="transition-colors hover:text-white">{t.landing.footer.careers}</a></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.contact}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.legal}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><a href="/privacy" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">{t.landing.footer.privacyPolicy}</a></li>
                <li><a href="/terms" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">{t.landing.footer.termsOfService}</a></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.securityLink}</button></li>
                <li><a href="mailto:support@enrola.com?subject=GDPR" className="transition-colors hover:text-white">{t.landing.footer.gdpr}</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-secondary-800 pt-5 sm:flex-row">
            <p className="text-xs text-secondary-500">
              {t.landing.footer.copyright}
            </p>
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-secondary-400" />
              <span className="text-xs text-secondary-400">
                {t.landing.footer.security}
              </span>
            </div>
          </div>
        </div>
            </SceneReveal>

            <SceneReveal as="footer" className="relative z-10 hidden w-full rounded-3xl bg-secondary-900 px-8 py-10 text-white lg:block">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 grid gap-10 md:grid-cols-4">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-6">
                <img
                  src="/logo.png"
                  alt={t.brand?.logoAlt || 'Enrola Logo'}
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="mb-6 text-secondary-400">
                {t.landing.footer.description}
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://enrolanow.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-400 transition-colors hover:text-white"
                  aria-label={t.landing.footer.visitWebsite}
                >
                  <Globe className="h-5 w-5" />
                </a>
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className="text-secondary-400 transition-colors hover:text-white"
                  aria-label={t.landing.footer.securityInfo}
                >
                  <Lock className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.product}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('features')}>{t.landing.footer.features}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('pricing')}>{t.landing.footer.pricing}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('faq')}>{t.landing.footer.integrations}</button></li>
                <li><button type="button" className="transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.api}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.company}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('features')}>{t.landing.footer.aboutUs}</button></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('faq')}>{t.landing.footer.blog}</button></li>
                <li><a href="mailto:sales@enrola.com" className="transition-colors hover:text-white">{t.landing.footer.careers}</a></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.contact}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-4 font-bold">{t.landing.footer.legal}</h4>
              <ul className="space-y-3 text-secondary-400">
                <li><a href="/privacy" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">{t.landing.footer.privacyPolicy}</a></li>
                <li><a href="/terms" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">{t.landing.footer.termsOfService}</a></li>
                <li><button type="button" className="text-left transition-colors hover:text-white" onClick={() => scrollToSection('contact')}>{t.landing.footer.securityLink}</button></li>
                <li><a href="mailto:support@enrola.com?subject=GDPR" className="transition-colors hover:text-white">{t.landing.footer.gdpr}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-secondary-800 pt-6">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <p className="text-sm text-secondary-400">
                {t.landing.footer.copyright}
              </p>
              <div className="mt-3 flex items-center space-x-6 md:mt-0">
                <Shield className="h-5 w-5 text-secondary-400" />
                <span className="text-sm text-secondary-400">
                  {t.landing.footer.security}
                </span>
              </div>
            </div>
          </div>
        </div>
            </SceneReveal>
            </SceneGroup>
          </SceneShell>
        </ViewportScene>
      </ViewportScenes>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-elegant transition-[box-shadow,transform] duration-300 hover:scale-110 max-lg:safe-inset-bottom max-lg:bottom-6 max-lg:right-4 max-lg:h-12 max-lg:w-12"
          aria-label="Scroll to top"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default LandingPage;

